import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { X, Send, Sparkles, Loader2, RotateCcw } from 'lucide-react';
import { useAuth } from '../AuthContext';

/**
 * Floating "Ask nischals" AI helper. Powered by Gemini 3 Flash via /api/ai/chat.
 * Session id persists in sessionStorage so context survives page reloads within a tab.
 */
const AskNischalsWidget = () => {
  const { user } = useAuth();
  const location = useLocation();
  const params = useParams();
  const [open, setOpen] = useState(false);

  // Only admins get the AI helper (cost-control decision — teachers/parents don't need it).
  const ALLOWED_ROLES = ['master_admin', 'super_admin', 'school_admin', 'group_admin'];
  const isAllowed = !!user && (
    (user.is_master_admin === true) ||
    ALLOWED_ROLES.includes(user.role)
  );
  const [messages, setMessages] = useState(() => {
    try {
      const raw = sessionStorage.getItem('ask_nischals_msgs');
      return raw ? JSON.parse(raw) : DEFAULT_INTRO;
    } catch { return DEFAULT_INTRO; }
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem('ask_nischals_sid') || null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    try { sessionStorage.setItem('ask_nischals_msgs', JSON.stringify(messages)); } catch { /* noop */ }
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (sessionId) sessionStorage.setItem('ask_nischals_sid', sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (open && inputRef.current) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  // Derive live context to help the AI answer questions like "how is my school doing?"
  const context = React.useMemo(() => {
    const ctx = {};
    if (user?.role) ctx.role = user.role;
    const path = location.pathname || '';
    if (path.startsWith('/master-dashboard')) ctx.page = 'Master Dashboard';
    else if (path.startsWith('/super-admin')) ctx.page = 'State / District Admin Dashboard';
    else if (path.startsWith('/group-admin')) ctx.page = 'School Chain (Group Admin) Dashboard';
    else if (path.startsWith('/teacher')) ctx.page = 'Teacher Dashboard';
    else if (path.startsWith('/school-admin')) ctx.page = 'School Admin Dashboard';
    else if (path.startsWith('/school/')) ctx.page = 'School Detail';
    else if (path.startsWith('/district/')) ctx.page = 'District Detail';
    else if (path === '/' || path === '') ctx.page = 'Landing Page';
    const m = path.match(/^\/school\/([\w-]+)/);
    if (m) ctx.school_udise = m[1];
    return ctx;
  }, [user, location.pathname, params]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || loading) return;
    const userMsg = { role: 'user', content: trimmed };
    // Push user + empty assistant placeholder we'll fill via streaming
    setMessages((m) => [...m, userMsg, { role: 'assistant', content: '', streaming: true }]);
    setInput('');
    setLoading(true);

    const priorUserTurns = messages
      .filter((m) => m.role === 'user' && !m.intro)
      .slice(-6)
      .map((m) => ({ role: m.role, content: m.content }));

    const backend = process.env.REACT_APP_BACKEND_URL || '';
    let receivedText = '';
    let newSessionId = sessionId;

    try {
      const token = localStorage.getItem('access_token');
      const resp = await fetch(`${backend}/api/ai/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          session_id: sessionId || undefined,
          message: trimmed,
          history: sessionId ? [] : priorUserTurns,
          context,
        }),
      });

      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buf = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // Parse SSE frames (blank-line separated)
        const frames = buf.split('\n\n');
        buf = frames.pop() || '';
        for (const frame of frames) {
          const lines = frame.split('\n');
          const eventLine = lines.find((l) => l.startsWith('event:'));
          const dataLine = lines.find((l) => l.startsWith('data:'));
          if (!eventLine || !dataLine) continue;
          const ev = eventLine.replace(/^event:\s*/, '').trim();
          const data = dataLine.replace(/^data:\s*/, '');
          try {
            const payload = JSON.parse(data);
            if (ev === 'reply' && payload.reply) {
              receivedText = payload.reply;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: 'assistant', content: receivedText, streaming: true };
                return copy;
              });
            } else if (ev === 'thinking') {
              if (payload.session_id) newSessionId = payload.session_id;
            } else if (ev === 'done') {
              if (payload.session_id) newSessionId = payload.session_id;
            } else if (ev === 'error') {
              throw new Error(payload.error || 'AI error');
            }
          } catch (parseErr) {
            if (parseErr instanceof SyntaxError) continue;
            throw parseErr;
          }
        }
      }

      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: 'assistant',
          content: receivedText || 'Hmm, I had trouble answering that.',
        };
        return copy;
      });
      if (newSessionId && newSessionId !== sessionId) setSessionId(newSessionId);
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: 'assistant',
          content: 'Sorry, something went wrong reaching the assistant. Please try again in a moment.',
          error: true,
        };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  const resetConversation = () => {
    setMessages(DEFAULT_INTRO);
    setSessionId(null);
    sessionStorage.removeItem('ask_nischals_sid');
    sessionStorage.removeItem('ask_nischals_msgs');
  };

  // Hide the widget entirely for non-admin roles / anonymous visitors
  if (!isAllowed) return null;

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="ask-nischals-launcher"
          aria-label="Ask nischals AI"
          className="fixed bottom-5 right-5 z-40 group flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white font-semibold shadow-2xl shadow-fuchsia-400/40 hover:-translate-y-0.5 hover:shadow-fuchsia-500/60 transition-all"
        >
          <div className="relative">
            <Sparkles className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-300 rounded-full animate-pulse" />
          </div>
          <span className="hidden sm:inline text-sm">Ask nischals</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-4 right-4 z-50 w-[min(96vw,380px)] max-h-[80vh] flex flex-col rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4" data-testid="ask-nischals-panel">
          <div className="h-1 bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-amber-500" />
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-indigo-50/70 to-fuchsia-50/70">
            <div className="flex items-center gap-2 min-w-0">
              <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center text-white shadow-md flex-shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-900 leading-tight">Ask nischals</p>
                <p className="text-[11px] text-slate-500 leading-tight">Powered by Gemini · Answers about the app</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={resetConversation}
                data-testid="ask-nischals-reset"
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                title="New conversation"
              ><RotateCcw className="w-4 h-4" /></button>
              <button
                onClick={() => setOpen(false)}
                data-testid="ask-nischals-close"
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                title="Close"
              ><X className="w-4 h-4" /></button>
            </div>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50/60" data-testid="ask-nischals-messages">
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} error={m.error}>{m.content || (m.streaming ? '\u2026' : '')}</Bubble>
            ))}
            {loading && messages[messages.length - 1]?.streaming && !messages[messages.length - 1]?.content && (
              <div className="text-[11px] text-slate-500 pl-2">Ask nischals is thinking…</div>
            )}
            {/* Quick chips only when it's a fresh conversation */}
            {messages.length <= DEFAULT_INTRO.length && !loading && (
              <div className="flex flex-wrap gap-1.5 pt-2">
                {QUICK_PROMPTS.map((qp) => (
                  <button
                    key={qp}
                    onClick={() => send(qp)}
                    data-testid={`ask-nischals-quick-${qp.slice(0,20).replace(/\s+/g,'-').toLowerCase()}`}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                  >{qp}</button>
                ))}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-slate-100 p-2.5 flex items-center gap-2 bg-white"
          >
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about EduLab…"
              disabled={loading}
              data-testid="ask-nischals-input"
              className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="ask-nischals-send"
              className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white shadow-md disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg transition-all"
              aria-label="Send"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

const DEFAULT_INTRO = [{
  role: 'assistant',
  intro: true,
  content: "Hi! I'm nischals AI — your in-app helper. Ask me anything about the dashboard, roles, calendar, or how-to steps.",
}];

const QUICK_PROMPTS = [
  'How do I log a lab activity?',
  'How do I onboard a school?',
  'What does a Group Admin see?',
  'How does forgot password work?',
  'Where is the leaderboard?',
];

const Bubble = ({ role, error, children, loading }) => {
  const isUser = role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white rounded-br-md shadow-md'
            : error
              ? 'bg-rose-50 text-rose-700 border border-rose-200 rounded-bl-md'
              : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md shadow-sm'
        }`}
      >
        {loading ? children : children}
      </div>
    </div>
  );
};

export default AskNischalsWidget;
